<script setup lang="ts">
    import { Button } from '@/components/ui/button';
    import {
        Dialog,
        DialogContent,
        DialogHeader,
        DialogTitle,
        DialogClose,
        DialogFooter,
        DialogBody,
    } from '@/components/ui/dialog';
    import { computed, ref, watch } from 'vue';
    import { useI18n } from 'vue-i18n';
    import FileUploadZone from '../FileUploadZone.vue';
    import type { Payslip } from '@/types';
    import axios from 'axios';
    import { usePage } from '@inertiajs/vue3';
    import type { SharedData } from '@/types';
    import { Checkbox } from '@/components/ui/form';
    import { useQueryClient } from '@tanstack/vue-query';
    import QueryKey from '@/enums/QueryKey';
    import { toast } from 'vue-sonner';

    const props = defineProps<{
        payslip: Payslip;
        modelValue?: boolean;
    }>();

    const emit = defineEmits<{
        'update:modelValue': [value: boolean];
    }>();

    const open = computed({
        get: () => props.modelValue ?? false,
        set: (value) => emit('update:modelValue', value),
    });

    const { t } = useI18n();

    const page = usePage<SharedData>();
    const selectedCompanyName = computed(() => page.props.auth.user.selected_company_name);
    const selectedCompanyId = computed(() => page.props.auth.user.selected_company_id);

    const uploadedFile = ref([]);
    const consent = ref(false);

    const queryClient = useQueryClient();

    const handleClose = () => {
        uploadedFile.value = [];
        consent.value = false;
        open.value = false;
    };

    const handleFileDelete = async () => {
        await axios.post(route('v2.salary.payslip.delete'), { id: props.payslip.id });
    };

    const handleSuccess = () => {
        toast.success(t('forms.general.success'));
    };

    watch(open, (newVal) => {
        if (!newVal) {
            queryClient.invalidateQueries({
                queryKey: [QueryKey.PAYROLLS, selectedCompanyId.value],
            });
        }
    });
</script>

<template>
    <Dialog
        :open="open"
        @update:open="(value) => (open = value)"
    >
        <DialogContent class="sm:max-w-lg">
            <DialogHeader>
                <DialogTitle>
                    {{ t('pay-slips.headers.upload') }}
                </DialogTitle>
            </DialogHeader>

            <DialogBody class="space-y-5">
                <Checkbox
                    id="consent"
                    v-model="consent"
                >
                    <i18n-t
                        keypath="pay-slips.upload_consent"
                        tag="span"
                        scope="global"
                    >
                        <template #name>
                            <span class="font-bold">
                                {{ selectedCompanyName }}
                            </span>
                        </template>
                    </i18n-t>
                </Checkbox>

                <FileUploadZone
                    v-if="consent"
                    v-model="uploadedFile"
                    ref="uploadZoneRef"
                    instant-upload
                    :route="route('v2.salary.payslip.upload')"
                    :additional-fields="{
                        id: payslip.id,
                    }"
                    :max-uploads="1"
                    :delete-file="handleFileDelete"
                    @success="handleSuccess"
                />
            </DialogBody>

            <DialogFooter class="gap-2 border-t sm:justify-center">
                <DialogClose as-child>
                    <Button
                        variant="outline"
                        size="sm"
                        @click="handleClose"
                    >
                        {{ t('forms.close') }}
                    </Button>
                </DialogClose>
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>
